import React from 'react';
import { Alert, Button, Card, Progress, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleOutlined, EyeOutlined } from '@ant-design/icons';
import type { Product } from '../../types/product';
import type { ProductDuplicateGroup } from '../../types/productDuplicate';
import {
  buildCompareRows,
  CompareRow,
  hasRangeDifferentShippingScope,
  levelMeta,
  sourceLabels,
  statusLabels,
} from './duplicateProductUtils';

type Props = {
  group: ProductDuplicateGroup;
  resolvingKey: string | null;
  onShowProduct: (product: Product) => void;
  onResolveRecommended: (group: ProductDuplicateGroup) => void;
  onResolve: (group: ProductDuplicateGroup, keepProductId: number) => void;
  onGroupAction: (group: ProductDuplicateGroup, action: 'ignore' | 'restore') => void;
};

const DuplicateGroupCard: React.FC<Props> = ({
  group,
  resolvingKey,
  onShowProduct,
  onResolveRecommended,
  onResolve,
  onGroupAction,
}) => {
  const products = group.products || [];
  const meta = levelMeta[group.similarity_level] || levelMeta.medium;
  const rangeDifferent = hasRangeDifferentShippingScope(group.different_fields);
  const columns: ColumnsType<CompareRow> = [
    { title: '字段', dataIndex: 'field', width: 120, fixed: 'left' },
    ...products.map((product) => ({
      title: (
        <Space direction="vertical" size={2}>
          <Space wrap size={4}>
            <span>#{product.id}</span>
            <Tag color={product.source_platform === '172' ? 'orange' : 'blue'}>
              {sourceLabels[product.source_platform || ''] || product.source_platform || '-'}
            </Tag>
            {group.recommendation_scores?.[product.id] != null && (
              <Tag color="cyan">推荐分 {group.recommendation_scores?.[product.id]}</Tag>
            )}
            {group.recommended_keep_product_id === product.id && <Tag color="green">建议保留</Tag>}
          </Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onShowProduct(product)} style={{ padding: 0 }}>
            查看详情
          </Button>
        </Space>
      ),
      dataIndex: ['values', product.id],
      key: String(product.id),
      width: 220,
      render: (_: unknown, row: CompareRow) => row.values[product.id],
    })),
  ];

  return (
    <Card
      key={group.group_key}
      className="duplicate-product-card"
      title={
        <div className="duplicate-product-summary">
          <Space>
            <span>重复组 {group.group_key}</span>
            <Tag color={meta.color}>{meta.label}</Tag>
            {rangeDifferent && <Tag color="gold">地区范围不同</Tag>}
            {rangeDifferent && <Tag color="purple">需人工确认</Tag>}
            <Tag>{statusLabels[group.status] || group.status}</Tag>
          </Space>
          <Space>
            <Progress percent={group.similarity_score} size="small" status={meta.status} style={{ width: 160 }} />
            <Typography.Text strong>{group.similarity_score}%</Typography.Text>
          </Space>
        </div>
      }
    >
      <div className="duplicate-product-reasons">
        <Space wrap>
          {(group.match_reasons || []).map((reason) => <Tag key={reason} color="green">{reason}</Tag>)}
          {(group.different_fields || []).map((field) => <Tag key={field} color="orange">{field}不同</Tag>)}
        </Space>
      </div>

      {(group.recommendation_reasons || []).length > 0 && (
        <div className="duplicate-product-recommendation">
          <Typography.Text type="secondary">推荐理由：</Typography.Text>
          <Space wrap size={4}>
            {group.recommendation_basis && <Tag color="blue">{group.recommendation_basis}</Tag>}
            {(group.recommendation_reasons || []).map((reason) => <Tag key={reason} color="cyan">{reason}</Tag>)}
          </Space>
        </div>
      )}

      {group.resolution_note && <Alert type="info" showIcon message={group.resolution_note} style={{ marginBottom: 12 }} />}
      {rangeDifferent && group.status === 'candidate' && (
        <Alert
          type="warning"
          showIcon
          message="该候选的可办地区范围不同，不参与一键自动处理；请人工确认后选择要保留的套餐。"
          style={{ marginBottom: 12 }}
        />
      )}

      <Table rowKey="key" columns={columns} dataSource={buildCompareRows(products)} size="small" bordered pagination={false} scroll={{ x: 'max-content' }} />

      <div className="duplicate-product-actions">
        <Tooltip title={rangeDifferent ? '地区范围不同，推荐保留不自动处理，请人工选择保留套餐' : undefined}>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={resolvingKey === group.group_key}
            disabled={group.status === 'confirmed' || !group.recommended_keep_product_id || rangeDifferent}
            onClick={() => onResolveRecommended(group)}
          >
            保留推荐套餐
          </Button>
        </Tooltip>
        {products.map((product) => (
          <Button key={product.id} loading={resolvingKey === group.group_key} disabled={group.status === 'confirmed'} onClick={() => onResolve(group, product.id)}>
            保留 #{product.id}，隐藏其他
          </Button>
        ))}
        <Button loading={resolvingKey === group.group_key} disabled={group.status === 'confirmed'} onClick={() => onGroupAction(group, 'ignore')}>
          都保留/忽略
        </Button>
        {group.status === 'confirmed' && (
          <Button loading={resolvingKey === group.group_key} onClick={() => onGroupAction(group, 'restore')}>
            恢复展示
          </Button>
        )}
      </div>
    </Card>
  );
};

export default DuplicateGroupCard;
